import { useCallback } from 'react'

import { Controller, useFormContext } from 'react-hook-form'

import PauseIcon from '@/assets/icons/icon-pause.svg'
import PlayIcon from '@/assets/icons/icon-play.svg'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx'
import { Slider } from '@/components/ui/slider.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import { audios } from '@/modules/creator-settings/data/audios.ts'
import { SettingsOption } from '@/modules/creator-settings/ui/SettingsOption'
import { debounce } from '@/utils/helpers'

import { usePlayAudio } from '../hooks/use-audio'
import { useDisplaySettingsMutations } from '../hooks/use-display-settings-mutations.tsx'

export const CountdownAudio = () => {
  const { control, watch } = useFormContext()
  const { countdownAudio, countdownAudioVolume } = useDisplaySettingsMutations()

  const toast = useToast()

  const { audioCountdownSound, audioCountdownVolume } = watch()

  const { togglePlay, playing } = usePlayAudio(
    audioCountdownSound,
    audioCountdownVolume,
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleAudioChange = useCallback(
    debounce((audio: string) => {
      const updateAudio = countdownAudio.mutateAsync(audio)

      updateAudio.then(() =>
        toast.success('Successfully updated Countdown Audio'),
      )
    }, 800),
    [],
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleAudioVolumeChange = useCallback(
    debounce((volume: number[]) => {
      const updateVolume = countdownAudioVolume.mutateAsync(volume[0])

      updateVolume.then(() =>
        toast.success('Successfully updated Countdown Volume'),
      )
    }, 800),
    [],
  )

  return (
    <SettingsOption
      title="Countdown Audio"
      mobileVertical
      description={
        <div className="flex items-center gap-1">
          <span>Select the sound that is played when a countdown ends</span>
        </div>
      }
    >
      <div className="grid grid-flow-col items-center gap-4 rounded-xl bg-card px-6 py-2">
        <div onClick={togglePlay}>
          {playing ? (
            <PauseIcon
              className="cursor-pointer text-primary-500"
              height={17}
              width={17}
            />
          ) : (
            <PlayIcon
              className="cursor-pointer text-primary-500"
              height={12}
              width={12}
            />
          )}
        </div>

        <span className="pl-1 text-border">|</span>

        <Controller
          name="audioCountdownSound"
          control={control}
          render={({ field }) => (
            <Select
              value={field.value}
              onValueChange={(value) => {
                field.onChange(value)
                handleAudioChange(value)
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {audios.map((option) => (
                    <SelectItem
                      key={option.id}
                      value={option.id}
                    >
                      {option.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          )}
        />

        <span className="pl-1 text-border">|</span>

        <Controller
          name="audioCountdownVolume"
          control={control}
          render={({ field }) => (
            <Slider
              min={0}
              max={100}
              step={1}
              className="w-52"
              value={field.value}
              onValueCommit={field.onBlur}
              onValueChange={(volume) => {
                field.onChange(volume)
                handleAudioVolumeChange(volume)
              }}
            />
          )}
        />
      </div>
    </SettingsOption>
  )
}
